import { SDK, ServerOptions } from '@commercetools/frontend-sdk';
import { ComposableCommerceEventsB2B } from '../../types/events/ComposableCommerceEventsB2B';
import {
  GetProductAction,
  GetProductFiltersAction,
  ProductPricesBySku,
  ProductQueryAction,
  ProductStockBySku,
  QueryProductCategoriesAction,
  QueryProductPricesAction,
  QueryProductStockAction,
} from '../../types/actions/ProductActions';
import {
  GetProductQuery,
  ProductQueryPricesQuery,
  ProductQueryQuery,
  ProductQueryStockQuery,
  QueryProductCategoriesQuery,
} from '../../types/queries/ProductQueries';
import { Category, FilterField, Product } from '@shared/types/product';
import { PaginatedResult, ProductPaginatedResult } from '@shared/types/result';

export type ProductActions = {
  getProduct: GetProductAction;
  query: ProductQueryAction;
  queryPrices: QueryProductPricesAction;
  queryStock: QueryProductStockAction;
  queryCategories: QueryProductCategoriesAction;
  getProductFilters: GetProductFiltersAction;
};

export const getProductActions = (sdk: SDK<ComposableCommerceEventsB2B>): ProductActions => {
  return {
    getProduct: async (
      query: GetProductQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<Product>({
        actionName: 'product/getProduct',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    query: async (
      query?: ProductQueryQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<ProductPaginatedResult>({
        actionName: 'product/query',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    queryCategories: async (
      query?: QueryProductCategoriesQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<PaginatedResult<Category>>({
        actionName: 'product/queryCategories',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    queryPrices: async (
      query: ProductQueryPricesQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      // SDK serialises array params as repeated keys (e.g. `skus=A&skus=B`).
      // The BFF handler accepts either repeated-key arrays or a single
      // comma-separated string, so this works without further encoding.
      const response = await sdk.callAction<ProductPricesBySku>({
        actionName: 'product/queryPrices',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    // EWM-633 — stock-only sibling of queryPrices. Same query-serialisation
    // (array SKUs as repeated keys); the FE uses it for the live (later
    // short-TTL-cached) stock read, separate from the cached price read.
    queryStock: async (
      query: ProductQueryStockQuery,
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<ProductStockBySku>({
        actionName: 'product/queryStock',
        query,
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
    getProductFilters: async (
      options: {
        parallel?: boolean;
        customHeaderValue?: string;
        serverOptions?: ServerOptions;
      } = {},
    ) => {
      const response = await sdk.callAction<FilterField[]>({
        actionName: 'product/productFilters',
        parallel: options.parallel,
        customHeaderValue: options.customHeaderValue,
        serverOptions: options.serverOptions,
      });
      return response;
    },
  };
};
